import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import './ChatWidget.css';
import { useTranslation } from 'react-i18next';

const ChatWidget = ({ fullscreen = false, openByDefault = false, initialMessages = [] }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(fullscreen ? true : openByDefault);
  const [messages, setMessages] = useState(
    initialMessages.length
      ? initialMessages.map(msg => ({ from: 'bot', text: msg.content || msg.text }))
      : [{ from: 'bot', text: t('chat.accroche') }]
  );
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const toggleChat = () => setOpen(!open);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { from: 'user', text: input };
    const updatedMessages = [...messages, userMessage];

    setMessages([...updatedMessages, { from: 'bot', text: '...' }]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('https://n8n.atoneo.com/webhook/1be9a448-12b7-48c5-9409-aa59a850f63d', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: updatedMessages })
      });

      if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`);

      const contentType = res.headers.get('Content-Type') || '';
      let botText = 'Réponse non-JSON reçue du serveur.';

      if (contentType.includes('application/json')) {
        const data = await res.json();

        if (typeof data.text === 'string') {
          botText = data.text;
        } else if (
          Array.isArray(data) &&
          data[0]?.output?.text
        ) {
          botText = data[0].output.text;
        }
      }

      setMessages((prev) => [
        ...prev.slice(0, -1),
        { from: 'bot', text: botText }
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev.slice(0, -1),
        {
          from: 'bot',
          text: `Erreur : ${err.message}`
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  return (
    <div className={`chat-widget ${fullscreen ? 'fullscreen' : ''}`}>
      {!fullscreen && (
        <button
          className={`chat-toggle ${!open ? 'pulsing' : ''}`}
          onClick={toggleChat}
        >
          <span>{t('chat.nom')}</span>
        </button>
      )}

      {open && (
        <div className="chat-window">
          {!fullscreen && (
            <button className="chat-close" onClick={toggleChat}>×</button>
          )}
          <div className="chat-messages">
            {messages.map((msg, idx) => (
              <div key={idx} className={`chat-message ${msg.from}`}>
                {msg.text === '...' && loading ? (
                  <span className="dots">
                    <span>.</span>
                    <span>.</span>
                    <span>.</span>
                  </span>
                ) : (
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div className="chat-input">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('chat.placeholder') || "Écrivez un message..."}
            />
            <button onClick={handleSend}>{t('chat.send') || 'Envoyer'}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
